import cx from 'classnames';
import {useEffect, useCallback, useState, useContext} from 'react';

import {Loader} from 'components';
import {UserSessionContext} from 'context';
import {SessionScreenshot} from 'interfaces';

type Props = {
    screenshot: SessionScreenshot;
    hidden?: boolean; // used to not reload a preview when toggle a visibility
    errorClassName?: string;
};

export const ScreenshotPreview: React.FC<Props> = ({screenshot, hidden = false, errorClassName = ''}: Props) => {
    const {loadScreenshotPreview} = useContext(UserSessionContext);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewLoadingError, setPreviewLoadingError] = useState(false);
    const [screenshotBase64, setScreenshotBase64] = useState<string>(null);

    const setScreenshotPreview = useCallback(() => {
        setPreviewLoading(true);
        loadScreenshotPreview(screenshot).then(({content}) => {
            setScreenshotBase64(content);
        }).catch(() => {
            setScreenshotBase64(null);
            setPreviewLoadingError(true);
        }).finally(() => {
            setPreviewLoading(false);
        });
    }, [screenshot, loadScreenshotPreview]);

    useEffect(() => {
        if (previewLoading) {
            return;
        }
        if (screenshot.received && !previewLoadingError && !screenshotBase64 && !hidden) {
            setScreenshotPreview();
        }
    }, [screenshot, previewLoadingError, screenshotBase64, previewLoading, hidden, setScreenshotPreview]);

    useEffect(() => {
        if (!screenshot.received && screenshotBase64) {
            setScreenshotBase64(null);
        }
    }, [screenshot, screenshotBase64]);

    if (hidden) {
        return null;
    }

    return screenshotBase64 || previewLoadingError ?
        <img
            src={screenshotBase64}
            alt={screenshot?.name}
            className={cx('tw-max-w-full tw-max-h-full tw-text-nowrap',
                {'tw-p-2': previewLoadingError, [errorClassName]: previewLoadingError})}
        /> :
        <div className='tw-flex-center tw-h-full tw-w-full tw-relative tw-min-h-8'>
            <Loader className='tw-max-w-12 tw-max-h-12' svgClassName='tw-w-8 tw-h-8' />
        </div>;
};
